import * as React from 'react';
import InputMask from 'react-input-mask';
import { cn } from '../../helpers/shadcnUtils.helper';
import SpinnerIcon from '../icon/Spinner';
import { Eye24Filled, EyeOff24Filled } from '@fluentui/react-icons';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string; // Floating label text
  error?: string; // Optional error message
  icon?: React.ReactNode; // Icon element
  iconPosition?: 'start' | 'end'; // Icon position
  actionButton?: React.ReactNode; // Generic action button
  onActionClick?: () => void; // Callback for action button click
  maskType?: 'currency' | 'phone' | 'bank'; // Mask type
  isLoading?: boolean; // Loading state
  validationState?: 'success' | 'error' | 'none'; // New prop for green/red styling
  disableBg?: boolean; // Disable background color
  inputSize?: 'md' | 'sm'; // 'md' for h-14, 'sm' for h-8
  isDirty?: boolean; // When true, shows blue border (modified state)
}

// Define masks for different types
const MASKS: Record<string, string> = {
  phone: '999 99 99 99',
  bank: '9999 9999 9999 9999',
  currency: '9 999 999',
};

// Function to dynamically update currency mask based on length
const getMask = (value: string): string => {
  const length = value.replace(/\s/g, '').length; // Remove spaces for length calculation
  switch (length % 3) {
    case 1:
      return '9 999 999 999 999 999 999 999 999';
    case 2:
      return '99 999 999 999 999 999 999 999 999';
    default:
      return '999 999 999 999 999 999 999 999';
  }
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      id,
      className,
      icon,
      iconPosition = 'start',
      actionButton,
      onActionClick,
      validationState,
      isDirty,
      value = '',
      onBlur,
      onChange,
      maskType,
      isLoading,
      disabled,
      disableBg,
      inputSize,
      ...props
    },
    ref,
  ) => {
    const [dynamicMask, setDynamicMask] = React.useState(MASKS.currency);

    // Update the currency mask dynamically when the value changes
    const updateMask = React.useCallback((inputValue: string) => {
      setDynamicMask(getMask(inputValue));
    }, []);

    React.useEffect(() => {
      if (maskType === 'currency' && value) {
        updateMask(value.toString());
      }
    }, [value, maskType, updateMask]);

    // Handle blur event
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
    };

    // Handle action button click
    const handleActionClick = (
      e: React.MouseEvent<HTMLButtonElement | HTMLSpanElement>,
    ) => {
      e.preventDefault();
      onActionClick?.();
    };
    const mask =
      maskType === 'currency'
        ? dynamicMask
        : maskType
        ? MASKS[maskType]
        : undefined;

    const heightClass = inputSize === 'sm' ? 'h-12' : 'h-14';

    return (
      <div>
        <div className="relative w-full">
          {/* Icon */}
          {icon && (
            <span
              className={cn(
                'absolute text-gray-400 -translate-y-1/2 top-1/2 z-10',
                iconPosition === 'start' ? 'left-3' : 'right-3',
              )}
            >
              {icon}
            </span>
          )}

          {/* Masked Input */}
          {mask ? (
            <InputMask
              mask={mask}
              onChange={onChange}
              value={value?.toString()}
              onBlur={handleBlur}
              maskChar={null}
              alwaysShowMask={false}
              inputMode={maskType === 'currency' ? 'numeric' : undefined}
              disabled={disabled}
            >
              {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
                <input
                  {...inputProps}
                  {...props}
                  className={cn(
                    `peer block w-full pt-4 pb-1 text-base bg-neutral-98 hover:bg-blue-95 rounded-xl focus:border focus:border-primary focus-visible:outline-none disabled:cursor-not-allowed  disabled:bg-neutral-98 disabled:text-neutral-80 [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#F8F7FB] [&:-webkit-autofill:hover]:shadow-[inset_0_0_0_1000px_#ECF0FF]`,
                    heightClass,
                    // Dynamic padding based on icon position
                    icon && iconPosition === 'start' ? 'pl-10 pr-3' : 'pl-3',
                    icon && iconPosition === 'end'
                      ? 'pr-10'
                      : actionButton
                      ? 'pr-12'
                      : '',
                    !icon && !actionButton ? 'px-3' : '',
                    error
                      ? 'border-red-500 focus-visible:ring-red-500'
                      : 'focus-visible:ring-primary',
                    value ? 'border-transparent' : '',
                    validationState === 'success'
                      ? '!border-green-500 !focus-visible:ring-green-500'
                      : '',
                    validationState === 'error'
                      ? 'border-red-500 focus-visible:ring-red-500'
                      : '',
                    isDirty &&
                    validationState !== 'success' &&
                    validationState !== 'error'
                      ? 'border border-primary'
                      : '',
                    disableBg ? 'bg-white' : 'bg-neutral-98',
                    className,
                    disabled ? 'cursor-not-allowed bg-neutral-95 text-neutral-60' : '',
                  )}
                  disabled={disabled}
                />
              )}
            </InputMask>
          ) : (
            // Regular Input if no mask is applied
            <input
              ref={ref}
              id={id}
              value={value}
              onBlur={handleBlur}
              onChange={onChange}
              inputMode={maskType === 'currency' ? 'numeric' : undefined}
              className={cn(
                `peer block w-full pt-1 pb-1 text-base bg-neutral-98 hover:bg-blue-95 rounded-xl focus:border focus:border-primary focus-visible:outline-none disabled:cursor-not-allowed  disabled:bg-neutral-98 disabled:text-neutral-80 [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#F8F7FB] [&:-webkit-autofill:hover]:shadow-[inset_0_0_0_1000px_#ECF0FF]`,
                heightClass,
                // Dynamic padding based on icon position
                icon && iconPosition === 'start' ? 'pl-10 pr-3' : 'pl-3',
                icon && iconPosition === 'end'
                  ? 'pr-10'
                  : actionButton
                  ? 'pr-12'
                  : '',
                !icon && !actionButton ? 'px-3' : '',
                error
                  ? 'border-red-500 focus-visible:ring-red-500'
                  : 'focus-visible:ring-primary',
                value ? 'border-transparent ' : '',
                validationState === 'success'
                  ? '!border-green-500 !focus-visible:ring-green-500'
                  : '',
                validationState === 'error'
                  ? 'border-red-500 focus-visible:ring-red-500'
                  : '',
                isDirty &&
                validationState !== 'success' &&
                validationState !== 'error'
                  ? 'border border-primary'
                  : '',
                disableBg ? 'bg-white' : 'bg-neutral-98',
                className,
                label?.length ? 'pt-4' : '',
              )}
              disabled={disabled}
              {...props}
            />
          )}

          {/* Floating Label */}
          <label
            htmlFor={id}
            className={cn(
              'absolute line-clamp-1 pointer-events-none top-1/2 -translate-y-1/2 text-base text-neutral-40 transition-all duration-300 transform origin-left scale-100 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:!top-1 peer-focus:translate-y-0 peer-focus:scale-75',
              icon && iconPosition === 'start' ? 'left-10' : 'left-3',
              value !== '' && value !== null
                ? inputSize === 'sm'
                  ? '!top-[0.15rem] translate-y-0 !scale-75'
                  : '!top-1 translate-y-0 !scale-75'
                : '',
              disabled ? 'text-neutral-80' : '',
            )}
          >
            {label}
          </label>

          {/* Action Button */}
          {actionButton && (
            <span
              className={cn(
                'absolute -translate-y-1/2 cursor-pointer top-1/2',
                icon && iconPosition === 'end' ? 'right-10' : 'right-3',
              )}
              onClick={handleActionClick}
            >
              {actionButton}
            </span>
          )}
          {isLoading && (
            <div
              className={cn(
                'absolute -translate-y-1/2 cursor-pointer top-1/2 text-blue-00',
                icon && iconPosition === 'end' ? 'right-10' : 'right-3',
              )}
            >
              <SpinnerIcon />
            </div>
          )}
        </div>

        {/* Error Message - show whenever there is an error (e.g. after submit), not only when touched */}
        {error && (
          <p className="mt-1 ml-4 text-xs text-left text-red-00">{error}</p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

const PasswordInput = React.forwardRef<HTMLInputElement, InputProps>(
  (props, ref) => {
    const [showPassword, setShowPassword] = React.useState(false);

    return (
      <Input
        ref={ref}
        {...props}
        type={showPassword ? 'text' : 'password'}
        actionButton={
          showPassword ? (
            <Eye24Filled fontSize={24} />
          ) : (
            <EyeOff24Filled fontSize={24} />
          )
        }
        onActionClick={() => setShowPassword((prev) => !prev)}
      />
    );
  },
);

export { Input, PasswordInput };
